// ============================================================
// useSectionCompletion.ts
// Per-case section completion tracking for the soft-guided flow.
//
// The case detail page renders sections sequentially: each one is
// locked until the previous is marked complete. Once a section is
// unlocked, it STAYS unlocked even if the user goes back to edit
// an earlier section (monotonic unlock).
//
// State model:
//   completedSections — set of section keys the user has explicitly
//                       marked complete. Toggled by markComplete /
//                       markIncomplete ("Edit details").
//   unlockedSections  — set of section keys past their lock gate.
//                       Grown monotonically: marking N complete
//                       adds N+1 to this set; markIncomplete never
//                       removes from this set.
//   currentCaseId     — the case currently hydrated. Set by
//                       loadForCase so mutations know which
//                       localStorage entry to write.
//
// Persistence: each case gets its own localStorage entry under
// `casevo_section_state_<caseId>` so completion + unlock state
// survives a refresh and is isolated per case.
// ============================================================
import { create } from 'zustand'
import { updateCaseApi } from '../api/casevoApi'
import { flushAutoSave } from '../hooks/useAutoSave'
import { setCaseBaseUpdatedAt } from '../hooks/saveBaseVersion'

// ★ SERIALISED completion sync (fixes the "mark complete" 409 + freeze).
// Previously each click fired its own fire-and-forget PUT with NO concurrency
// base and never advanced the case's optimistic-concurrency base. That PUT
// bumped the case's updatedAt, so the NEXT autosave of the case (sent with the
// old base) 409'd → re-fetch → re-apply cycles, and rapid clicks raced several
// PUTs (a slower, older completedSections array could land last).
// Now: ONE chain per session; each step first flushes any pending case
// autosave (so the two writes can't interleave), then PUTs the LATEST
// completion set (coalesced — skipped if unchanged since the last successful
// send), then advances the case's concurrency base to the server's new
// updatedAt so the next autosave doesn't 409. Every step settles (errors are
// swallowed per step), so the chain can never wedge.
let syncChain: Promise<void> = Promise.resolve()
const lastSent = new Map<string, string>()
const syncCompletion = (caseId: string, getLatest: () => string[] | null): void => {
  syncChain = syncChain
    .then(async () => {
      try { await flushAutoSave() } catch { /* its own error handling */ }
      const latest = getLatest()
      if (!latest) return // user moved to another case — its own load owns state
      const key = JSON.stringify([...latest].sort())
      if (lastSent.get(caseId) === key) return
      const saved = await updateCaseApi(caseId, { completedSections: latest })
      lastSent.set(caseId, key)
      if (saved?.updatedAt) setCaseBaseUpdatedAt(caseId, saved.updatedAt)
    })
    .catch(() => { /* best-effort — UI already updated; next change re-sends */ })
}
// All sections that participate in the sequential flow. Order is
// the display order — completing one unlocks the next in this list
// (filtered by which are currently visible to the user).
//
// Non-sequential sections (Automatic, Settings) are deliberately
// absent — they're case metadata / admin tools, not data-entry
// steps, so they never lock and never get marked complete via this
// flow.
export type CompletableSection =
  | 'clientSource'
  | 'clientDetails'
  | 'accidentDetails'
  | 'injuryDetails'
  | 'witnesses'
  | 'defendant'
  | 'tpi'
  | 'linkedCases'
  | 'medicalAgency'
  | 'medicalExpert'
  | 'tps'
  | 'documents'
export const COMPLETABLE_SECTIONS: CompletableSection[] = [
  'clientSource',
  'clientDetails',
  'accidentDetails',
  'injuryDetails',
  'witnesses',
  'defendant',
  'tpi',
  'linkedCases',
  'medicalAgency',
  'medicalExpert',
  'tps',
  'documents',
]
// Initial unlocked set — Client Details is always unlocked from the
// start so the user has somewhere to begin.
const initialUnlocked = (): Set<CompletableSection> =>
  new Set<CompletableSection>(['clientSource'])
// ── localStorage persistence ─────────────────────────────────
const storageKey = (caseId: string) => `casevo_section_state_${caseId}`
type Persisted = {
  completed: CompletableSection[]
  unlocked: CompletableSection[]
}
const loadState = (caseId: string): {
  completed: Set<CompletableSection>
  unlocked: Set<CompletableSection>
} => {
  if (typeof window === 'undefined') {
    return { completed: new Set(), unlocked: initialUnlocked() }
  }
  const raw = window.localStorage.getItem(storageKey(caseId))
  if (!raw) {
    return { completed: new Set(), unlocked: initialUnlocked() }
  }
  try {
    const parsed = JSON.parse(raw) as Partial<Persisted>
    const completed = new Set<CompletableSection>(parsed.completed ?? [])
    // Always re-seed clientSource into unlocked so a corrupted or
    // partial entry can't leave the user with nothing editable.
    const unlocked = new Set<CompletableSection>(parsed.unlocked ?? [])
    unlocked.add('clientSource')
    return { completed, unlocked }
  } catch {
    return { completed: new Set(), unlocked: initialUnlocked() }
  }
}
const persistState = (
  caseId: string | null,
  completed: Set<CompletableSection>,
  unlocked: Set<CompletableSection>,
): void => {
  if (caseId === null || typeof window === 'undefined') return
  const payload: Persisted = {
    completed: Array.from(completed),
    unlocked: Array.from(unlocked),
  }
  try {
    window.localStorage.setItem(storageKey(caseId), JSON.stringify(payload))
  } catch {
    // Quota / disabled → degrade silently to in-memory state.
    // Section flow still works for this session.
  }
}
interface SectionCompletionStore {
  currentCaseId: string | null
  completedSections: Set<CompletableSection>
  unlockedSections: Set<CompletableSection>
  // Hydrate the in-memory sets for the given case. `sharedCompleted` is
  // the CASE's completedSections (from currentCase) — the shared source of
  // truth for completion, so all users see the same state. Falls back to
  // the per-user localStorage cache only when it's absent. No-op when the
  // requested caseId matches what's already loaded so re-renders from
  // other store mutations don't wipe UI state.
  loadForCase: (caseId: string, sharedCompleted?: string[]) => void
  // Mark a section complete. Also (optionally) mark the next
  // section in the chain as unlocked. The caller computes "next"
  // because visibility depends on viewingPaneToggles, which the
  // section store doesn't know about.
  markComplete: (section: CompletableSection, nextToUnlock?: CompletableSection) => void
  // Reverse markComplete. unlockedSections is monotonic, so this
  // ONLY removes from completedSections — subsequent sections
  // stay unlocked.
  markIncomplete: (section: CompletableSection) => void
  // Reset all flags (called when opening a new case if needed).
  applyRemoteCompletion: (
    caseId: string,
    sharedCompleted: string[],
    nextOf: (s: CompletableSection) => CompletableSection | undefined,
  ) => void
  resetAll: () => void
  completedCount: () => number
  isComplete: (section: CompletableSection) => boolean
  isUnlocked: (section: CompletableSection) => boolean
}
export const useSectionCompletion = create<SectionCompletionStore>((set, get) => ({
  currentCaseId: null,
  completedSections: new Set(),
  unlockedSections: initialUnlocked(),
  loadForCase: (caseId, sharedCompleted) => {
    // Same case already loaded → no-op ONLY when there's nothing fresher to
    // apply. With the case's server completion supplied (reopening A after
    // visiting the list, where someone else completed sections meanwhile),
    // re-seed — the early return used to leave stale ticks/locks.
    if (get().currentCaseId === caseId && sharedCompleted === undefined) return
    const local = loadState(caseId)
    // Shared per-case completion is the SOURCE OF TRUTH: when the case
    // provides it (always, via getCaseById/getCases), seed from it so
    // every user viewing the case sees the same completed sections. Fall
    // back to the per-user localStorage cache only when it's genuinely
    // absent (offline / a pre-existing row that never had it).
    const completed = sharedCompleted !== undefined
      ? new Set<CompletableSection>(
          sharedCompleted.filter((s): s is CompletableSection =>
            COMPLETABLE_SECTIONS.includes(s as CompletableSection)),
        )
      : local.completed
    // Unlock = clientSource (always) + the user's local monotonic view
    // progress + every shared-completed section (so a user arriving
    // mid-flow can edit whatever's already been marked complete).
    const unlocked = new Set<CompletableSection>(local.unlocked)
    unlocked.add('clientSource')
    completed.forEach((s) => unlocked.add(s))
    // ★ Re-sync the coalescing key to what the SERVER holds now. Without
    // this, a stale key from an earlier visit (another user changed the set
    // meanwhile) makes the next local change look "already sent" and the PUT
    // is silently skipped.
    if (sharedCompleted !== undefined) lastSent.set(caseId, JSON.stringify(Array.from(completed).sort()))
    else lastSent.delete(caseId)
    set({ currentCaseId: caseId, completedSections: completed, unlockedSections: unlocked })
  },
  markComplete: (section, nextToUnlock) =>
    set((state) => {
      // Rapid double-clicks: already complete (and next already unlocked) →
      // nothing to change, nothing to send.
      if (state.completedSections.has(section) && (!nextToUnlock || state.unlockedSections.has(nextToUnlock))) {
        return {}
      }
      const completed = new Set(state.completedSections)
      completed.add(section)
      const unlocked = new Set(state.unlockedSections)
      if (nextToUnlock) unlocked.add(nextToUnlock)
      persistState(state.currentCaseId, completed, unlocked)
      // Sync to backend so server-side progress bars stay accurate.
      // Fire-and-forget — UI already updated locally; a failed PUT
      // doesn't roll back. .catch() swallows so we don't get an
      // unhandled-rejection in the console.
      if (state.currentCaseId) {
        const caseId = state.currentCaseId
        syncCompletion(caseId, () =>
          get().currentCaseId === caseId ? Array.from(get().completedSections) : null)
      }
      return { completedSections: completed, unlockedSections: unlocked }
    }),
  markIncomplete: (section) =>
    set((state) => {
      const completed = new Set(state.completedSections)
      completed.delete(section)
      // Intentionally NOT touching unlockedSections — monotonic
      // unlock means editing an earlier section never re-locks
      // sections that have already been opened up.
      persistState(state.currentCaseId, completed, state.unlockedSections)
      // Sync to backend so server-side progress bars stay accurate.
      // Fire-and-forget for the same reasons as markComplete above.
      if (state.currentCaseId) {
        const caseId = state.currentCaseId
        syncCompletion(caseId, () =>
          get().currentCaseId === caseId ? Array.from(get().completedSections) : null)
      }
      return { completedSections: completed }
    }),
  // ★ LIVE: another user changed this case's completion (entity-changed case
  // update carrying completedSections). Replace the completed set with the
  // server's and unlock every completed section + the section after each (the
  // same unlock markComplete does locally), so the lock / ✓ / orange "current"
  // state matches the acting user's. No PUT back (it came from the server).
  applyRemoteCompletion: (caseId, sharedCompleted, nextOf) => {
    const state = get()
    if (state.currentCaseId !== caseId) return
    const completed = new Set<CompletableSection>(
      sharedCompleted.filter((s): s is CompletableSection => COMPLETABLE_SECTIONS.includes(s as CompletableSection)),
    )
    const unlocked = new Set(state.unlockedSections)
    completed.forEach((s) => {
      unlocked.add(s)
      const n = nextOf(s)
      if (n) unlocked.add(n)
    })
    lastSent.set(caseId, JSON.stringify(Array.from(completed).sort()))
    persistState(caseId, completed, unlocked)
    set({ completedSections: completed, unlockedSections: unlocked })
  },
  resetAll: () => set({ completedSections: new Set(), unlockedSections: initialUnlocked() }),
  completedCount: () => get().completedSections.size,
  isComplete: (section) => get().completedSections.has(section),
  isUnlocked: (section) => get().unlockedSections.has(section),
}))
